import type {
  ActiveLayer,
  Event,
  ExplainResponse,
  IndexResponse,
  Pillar,
  TimelineResponse,
  Weights,
} from "@nexus/contracts";

export type IndexRankingEntry = IndexResponse["ranking"][number];
export type TimelineEntry = TimelineResponse["entries"][number];

export interface LayerMeta {
  id: ActiveLayer;
  nameKo: string;
  nameEn: string;
  color: string;
  glowColor: string;
  description: string;
  iconName: string;
}

export const LAYER_METADATA: Record<ActiveLayer, LayerMeta> = {
  AI: {
    id: "AI",
    nameKo: "인공지능",
    nameEn: "Artificial Intelligence",
    color: "#6366f1", // Electric Indigo
    glowColor: "rgba(99, 102, 241, 0.4)",
    description: "프론티어 모델, AI 데이터센터, 추론/학습 생태계",
    iconName: "BrainCircuit",
  },
  CHIP: {
    id: "CHIP",
    nameKo: "반도체",
    nameEn: "Semiconductor",
    color: "#06b6d4", // Silicon Cyan
    glowColor: "rgba(6, 182, 212, 0.4)",
    description: "첨단 파운드리, 리소그래피, HBM 메모리, 패키징",
    iconName: "Cpu",
  },
  ENERGY: {
    id: "ENERGY",
    nameKo: "에너지",
    nameEn: "Clean & Nuclear Energy",
    color: "#f59e0b", // Solar Amber
    glowColor: "rgba(245, 158, 11, 0.4)",
    description: "SMR/원전, 전력망 용량, AI 전력 공급망",
    iconName: "Zap",
  },
  ROBOTICS: {
    id: "ROBOTICS",
    nameKo: "로보틱스",
    nameEn: "Autonomous Robotics",
    color: "#10b981", // Kinetic Emerald
    glowColor: "rgba(16, 185, 129, 0.4)",
    description: "휴머노이드, 산업 자동화, 피지컬 AI",
    iconName: "Bot",
  },
  CYBER: {
    id: "CYBER",
    nameKo: "사이버",
    nameEn: "Cyber Security & Ops",
    color: "#f43f5e", // Crimson Rose
    glowColor: "rgba(244, 63, 94, 0.4)",
    description: "국가 지원 위협(APT), 핵심 인프라 침해, 제로데이",
    iconName: "ShieldAlert",
  },
  HYBRID_WARFARE: {
    id: "HYBRID_WARFARE",
    nameKo: "하이브리드전",
    nameEn: "Hybrid Warfare",
    color: "#a855f7", // Plasma Violet
    glowColor: "rgba(168, 85, 247, 0.4)",
    description: "공급망 무기화, 인지전, 해저케이블 교란, 복합 안보",
    iconName: "RadioTower",
  },
};

export interface PillarMeta {
  id: Pillar;
  nameKo: string;
  nameEn: string;
  color: string;
}

export const PILLAR_METADATA: Record<Pillar, PillarMeta> = {
  AI: {
    id: "AI",
    nameKo: "인공지능",
    nameEn: "AI Stock",
    color: "#6366f1",
  },
  CHIP: {
    id: "CHIP",
    nameKo: "반도체",
    nameEn: "Chip Stock",
    color: "#06b6d4",
  },
  ENERGY: {
    id: "ENERGY",
    nameKo: "에너지",
    nameEn: "Energy Stock",
    color: "#f59e0b",
  },
  ROBOTICS: {
    id: "ROBOTICS",
    nameKo: "로보틱스",
    nameEn: "Robotics Stock",
    color: "#10b981",
  },
};

export const REL_TYPE_LABELS: Record<string, { labelKo: string; color: string; dash?: boolean }> = {
  supplies: { labelKo: "공급", color: "#10b981" },
  restricts: { labelKo: "통제/규제", color: "#ef4444" },
  invests_in: { labelKo: "투자", color: "#3b82f6" },
  allied_with: { labelKo: "동맹", color: "#8b5cf6" },
  competes_with: { labelKo: "경쟁", color: "#f59e0b", dash: true },
  depends_on: { labelKo: "의존", color: "#06b6d4" },
  hostile_to: { labelKo: "적대", color: "#dc2626", dash: true },
};

export interface UIState {
  activeLayers: Set<ActiveLayer>;
  selectedEntityId: string | null;
  selectedEventId: string | null;
  selectedEvent: Event | null;
  explainEntityId: string | null;
  explainData: ExplainResponse | null;
  weights: Weights;
  isSimulating: boolean;
  timelineDate: string; // ISO date or "2026-H1"
  searchQuery: string;
  isExplainOpen: boolean;
  isEventDetailOpen: boolean;
  isEntityDetailOpen: boolean;
}
